import { Files, HardDrive, FileCheck2, FileClock, FileX2, type LucideIcon } from 'lucide-react'
import { formatFileSize } from '@/utils/fileSize'
import { useTranslation } from '@/i18n'
import type { ReactNode } from 'react'
import type { CatalogStats } from '@/utils/catalogStats'
import { STATUS_META, type StatusMeta } from './statusMeta'

interface StatCardProps {
  label: string
  value: string | number
  icon: ReactNode
  iconBg: string
}

function StatCard({ label, value, icon, iconBg }: StatCardProps) {
  return (
    <div className="flex-1 flex flex-col gap-1 px-4 py-3">
      <span className="text-base text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2.5">
        <div className={`size-8 rounded-full flex items-center justify-center shrink-0 ${iconBg}`}>
          {icon}
        </div>
        <span className="text-lg font-medium">{typeof value === 'number' ? value.toLocaleString() : value}</span>
      </div>
    </div>
  )
}

/**
 * UI-26 review: the banner used to list its three status cards in fixed JSX,
 * so reordering STATUS_META would have moved the filter and left the banner
 * behind — silently defeating the shared order this is supposed to guarantee.
 * It maps over STATUS_META instead; the order now has one home.
 *
 * Icon and count stay local: STATUS_META is palette and naming, and pulling
 * lucide components or a CatalogStats shape into it would make a data module
 * depend on both.
 */
const STATUS_ICON: Record<StatusMeta['value'], LucideIcon> = {
  success: FileCheck2,
  processing: FileClock,
  failed: FileX2,
}

const STATUS_COUNT: Record<StatusMeta['value'], (s: CatalogStats) => number> = {
  success: (s) => s.processedCount,
  processing: (s) => s.processingCount,
  failed: (s) => s.failedCount,
}

export default function StatsBanner({ stats }: { stats: CatalogStats }) {
  const { t } = useTranslation()
  return (
    <div className="mx-6 mt-4 flex rounded-xl border border-[#e5e5e5] dark:border-dark-border divide-x divide-[#e5e5e5] dark:divide-dark-border">
      <StatCard
        label={t('data_catalog:stats.files')}
        value={stats.totalDocuments}
        icon={<Files className="size-4 text-muted-foreground" />}
        iconBg="bg-[#e5e5e5]"
      />
      <StatCard
        label={t('data_catalog:stats.size')}
        value={formatFileSize(stats.totalSizeBytes)}
        // FS-568: HardDrive (storage), not Database — the page topbar/nav already
        // uses Database for "Data", and Size showing the same icon read as a duplicate.
        icon={<HardDrive className="size-4 text-muted-foreground" />}
        iconBg="bg-[#e5e5e5]"
      />
      {STATUS_META.map((meta) => {
        const Icon = STATUS_ICON[meta.value]
        return (
          <StatCard
            key={meta.value}
            label={t(meta.labelKey as never)}
            value={STATUS_COUNT[meta.value](stats)}
            icon={<Icon className={`size-4 ${meta.iconClass}`} />}
            iconBg={meta.iconBg}
          />
        )
      })}
    </div>
  )
}
